import moment from 'moment';
import {
  CUSTOM_ROUTINE_TRIGGER,
  DayNavDirection,
  DayPlanEventIdNamespace,
  DayPlanEventSource,
  DayPlanTaskMetaKind,
  DaysOfWeek,
  GoogleCalendarEventStatus,
  PlannedBlockKeyPrefix,
  TimelineEntryKind
} from 'enums';
import {
  BLOCK_KEY_SEPARATOR,
  CUSTOM_ROUTINE_BLOCK_ID_PREFIX,
  CUSTOM_ROUTINE_EMOJI,
  DAY_NAV_DELTA,
  DAY_PLAN_ALL_TIMEBOXED_COPY_KEYS,
  DAY_PLAN_DATE_STRIP_LENGTH,
  DAY_PLAN_DATE_STRIP_RADIUS,
  DAY_PLAN_EMPTY_PLANNED_COPY_KEYS,
  DAY_PLAN_WEEK_LENGTH,
  DEEP_WORK_END_BUFFER_MINUTES,
  DEEP_WORK_MIN_BLOCK_MINUTES,
  EVENING_ROUTINE_BLOCK_ID,
  EVENING_ROUTINE_EMOJI,
  MORNING_ROUTINE_BLOCK_ID,
  MORNING_ROUTINE_EMOJI,
  MS_PER_MINUTE,
  MS_PER_SECOND,
  EVENT_COLOR_HEX_ALPHA,
  EVENT_ACCENT_LINK_COLOR_MIX
} from 'constants/day-plan';
import {
  DATE_FORMAT_YMD,
  INTEGRATION_PLATFORM,
  SHORT_DAY_NAME
} from 'constants/general';
import { CALENDAR_EVENTS_QUERY_PARAM } from 'constants/params';
import {
  BuildCalendarEventsParamsArgs,
  CalendarEvent,
  DayPlanAgendaEvent,
  DayPlanAgendaGoogleDetails,
  DayPlanEmptyStateCopyKeys,
  DayPlanOneOffEvent,
  DayPlanTaskGroup,
  DayPlanTaskMetaPart,
  DeepWorkRange,
  EventColor,
  EventSeriesOccurrence,
  GoogleCalendarEventPerson,
  PlanTaskArgs,
  RoutineActivity,
  RoutineBlock,
  TimelineEntry,
  TodoPlanningPatch
} from 'interfaces/day-plan.interface';
import { ActivityType, ToDoTask } from 'interfaces/common.interface';
import { Project } from 'interfaces/project.interface';
import { Calendars } from 'interfaces';
import { CustomRoutineType, SettingsType } from 'interfaces/setting.interface';
import { t } from 'services/i18n.service';
import { normalizeDaysOfWeekValue } from 'utils/daysOfWeek.util';
import { roundToDecimals } from 'utils/number.util';
import {
  addDaysToYMD,
  formatDateToYMD,
  formatDueDate,
  formatMillisToTimeOnly,
  formatOrdinalDayMonth,
  formatShortDayName,
  formatWeekdayDayMonthYear,
  getIsoWeekStartYMD,
  getTodayKey,
  getYMDRangeKeys,
  isDayToday
} from 'utils/time.util';
import COLOR, { UNTIMED_TASK_COLOUR_PALETTE } from 'constants/color';
import { hasAnyItems } from 'utils/array.util';

/**
 * Build a minimal PATCH body for Day Plan planning writes.
 * Only own properties present on `args` (not undefined) are included — matching
 * BE `wasFieldProvided` semantics so reorder never wipes `planned_block_key`.
 */
export const buildPlanningPatchPayload = (
  args: PlanTaskArgs
): TodoPlanningPatch => {
  const patch: TodoPlanningPatch = {};
  if (Object.prototype.hasOwnProperty.call(args, 'plannedDate')) {
    patch.planned_date = args.plannedDate ?? null;
  }
  if (Object.prototype.hasOwnProperty.call(args, 'plannedBlockKey')) {
    patch.planned_block_key = args.plannedBlockKey ?? null;
  }
  if (Object.prototype.hasOwnProperty.call(args, 'plannedOrder')) {
    patch.planned_order = args.plannedOrder ?? null;
  }
  return patch;
};

export const buildTaskUpsertPayload = ({
  task,
  plannedDate,
  plannedBlockKey,
  plannedOrder
}: PlanTaskArgs): ToDoTask => ({
  ...task,
  planned_date: plannedDate,
  planned_block_key:
    plannedBlockKey !== undefined
      ? plannedBlockKey
      : (task.planned_block_key ?? null),
  planned_order: plannedOrder ?? task.planned_order ?? null
});

export const dedupeEventsByExternalId = (
  events: CalendarEvent[]
): CalendarEvent[] => {
  const seen = new Set<string>();
  return events.filter((event) => {
    if (seen.has(event.external_id)) {
      return false;
    }
    seen.add(event.external_id);
    return true;
  });
};

export const normalizeEventColoursResponse = (
  data: unknown
): Record<string, string> => {
  if (data == null) {
    return {};
  }
  if (Array.isArray(data)) {
    return normalizeEventColourRows(data);
  }
  if (typeof data !== 'object') {
    return {};
  }
  const record = data as Record<string, unknown>;
  if (Array.isArray(record.data)) {
    return normalizeEventColourRows(record.data);
  }
  if (Array.isArray(record.event_colours)) {
    return normalizeEventColourRows(record.event_colours);
  }
  if (
    record.event_colours !== null &&
    typeof record.event_colours === 'object' &&
    !Array.isArray(record.event_colours)
  ) {
    return stringRecordFromUnknown(record.event_colours);
  }
  return stringRecordFromUnknown(record);
};

const normalizeEventColourRows = (rows: unknown[]): Record<string, string> => {
  const colours: Record<string, string> = {};
  rows.forEach((row) => {
    if (row === null || typeof row !== 'object') {
      return;
    }
    const item = row as Partial<EventColor>;
    if (
      typeof item.event_id === 'string' &&
      item.event_id.length > 0 &&
      typeof item.colour === 'string'
    ) {
      colours[item.event_id] = item.colour;
    }
  });
  return colours;
};

const stringRecordFromUnknown = (value: unknown): Record<string, string> => {
  if (value === null || typeof value !== 'object') {
    return {};
  }
  const colours: Record<string, string> = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, colour]) => {
    if (typeof colour === 'string') {
      colours[key] = colour;
    }
  });
  return colours;
};

export const getDayNavDelta = (direction: DayNavDirection): number | null => {
  if (direction === DayNavDirection.PREV) {
    return DAY_NAV_DELTA.PREV;
  }
  if (direction === DayNavDirection.NEXT) {
    return DAY_NAV_DELTA.NEXT;
  }
  return null;
};

export const shiftDay = (day: string, direction: DayNavDirection): string => {
  if (direction === DayNavDirection.TODAY) {
    return getTodayKey();
  }
  const delta = getDayNavDelta(direction);
  if (delta == null) {
    return day;
  }
  return addDaysToYMD(day, delta);
};

export const formatDayLabel = (day: string): string =>
  formatWeekdayDayMonthYear(day);

export const getWeekDayKeys = (day: string): string[] =>
  getYMDRangeKeys(getIsoWeekStartYMD(day), DAY_PLAN_WEEK_LENGTH);

/** Date-strip keys centered on a day (default: today), 3 days on each side. */
export const getCenteredDayStripKeys = (
  centerDay: string = getTodayKey()
): string[] =>
  getYMDRangeKeys(
    addDaysToYMD(centerDay, -DAY_PLAN_DATE_STRIP_RADIUS),
    DAY_PLAN_DATE_STRIP_LENGTH
  );

export const formatDayStripParts = (
  day: string
): { weekday: string; date: string } => ({
  weekday: formatShortDayName(day),
  date: formatOrdinalDayMonth(day)
});

export interface ParsedBlockKey {
  prefix: PlannedBlockKeyPrefix;
  value: string;
}

export const parsePlannedBlockKey = (
  key: string | null | undefined
): ParsedBlockKey | null => {
  if (!key) {
    return null;
  }
  const separatorIndex = key.indexOf(BLOCK_KEY_SEPARATOR);
  if (separatorIndex <= 0) {
    return null;
  }
  const rawPrefix = key.slice(0, separatorIndex);
  const value = key.slice(separatorIndex + 1);
  const matched = Object.values(PlannedBlockKeyPrefix).find(
    (prefix) => prefix === rawPrefix
  );
  if (!matched || !value) {
    return null;
  }
  return { prefix: matched, value };
};

export const isDayPlanEventTask = (task: ToDoTask): boolean =>
  parsePlannedBlockKey(task.planned_block_key)?.prefix ===
  PlannedBlockKeyPrefix.EVENT;

export const getDayPlanEventExternalId = (task: ToDoTask): string | null => {
  const parsed = parsePlannedBlockKey(task.planned_block_key);
  if (parsed?.prefix !== PlannedBlockKeyPrefix.EVENT) {
    return null;
  }
  return parsed.value;
};

export const getDayPlanEventTasks = (tasks: ToDoTask[]): ToDoTask[] =>
  tasks.filter((task) => {
    if (!isDayPlanEventTask(task)) {
      return false;
    }
    return getDayPlanEventExternalId(task) != null;
  });

/**
 * Split meeting todos into active vs dismissed using BE `dismissed_event_ids`.
 * Does not drop meetings from the day response — only buckets them for UI.
 */
export const partitionDayPlanEventTasks = ({
  tasks,
  dismissedEventIds = []
}: {
  tasks: ToDoTask[];
  dismissedEventIds?: string[];
}): { active: ToDoTask[]; dismissed: ToDoTask[] } => {
  const dismissedIds = new Set(dismissedEventIds);
  const active: ToDoTask[] = [];
  const dismissed: ToDoTask[] = [];
  getDayPlanEventTasks(tasks).forEach((task) => {
    const externalId = getDayPlanEventExternalId(task);
    if (externalId == null) {
      return;
    }
    if (dismissedIds.has(externalId)) {
      dismissed.push(task);
      return;
    }
    active.push(task);
  });
  return { active, dismissed };
};

export const resolveDayPlanEventSource = ({
  externalId,
  platform
}: {
  externalId: string;
  platform?: string | null;
}): DayPlanEventSource => {
  if (isImportedDayPlanEventId(externalId)) {
    return DayPlanEventSource.IMPORT;
  }
  if (platform === DayPlanEventSource.IMPORT) {
    return DayPlanEventSource.IMPORT;
  }
  if (platform === DayPlanEventSource.SERIES) {
    return DayPlanEventSource.SERIES;
  }
  if (platform != null && isGoogleCalendarPlatform(platform)) {
    return DayPlanEventSource.GOOGLE;
  }
  if (platform != null && isMicrosoftCalendarPlatform(platform)) {
    return DayPlanEventSource.MICROSOFT;
  }
  return DayPlanEventSource.UNKNOWN;
};

const durationSecondsFromIsoRange = (
  begins: string | null,
  ends: string | null
): number => {
  if (begins == null || ends == null || ends.length === 0) {
    return 0;
  }
  const start = moment(begins);
  const end = moment(ends);
  if (!start.isValid() || !end.isValid()) {
    return 0;
  }
  const seconds = Math.round(end.diff(start) / MS_PER_SECOND);
  return seconds > 0 ? seconds : 0;
};

const resolveGoogleOrganizerEmail = (
  organizer: GoogleCalendarEventPerson | undefined
): string | null => {
  if (organizer?.email == null || organizer.email.length === 0) {
    return null;
  }
  if (organizer.self === true) {
    return null;
  }
  return organizer.email;
};

const resolveGoogleDisplayStatus = (
  status: string | undefined
): string | null => {
  const rawStatus = status?.trim() ?? '';
  if (rawStatus.length === 0) {
    return null;
  }
  if (rawStatus.toLowerCase() === GoogleCalendarEventStatus.CONFIRMED) {
    return null;
  }
  return rawStatus;
};

export const buildDayPlanAgendaGoogleDetails = (
  event: CalendarEvent
): DayPlanAgendaGoogleDetails | null => {
  if (!isGoogleCalendarPlatform(event.platform)) {
    return null;
  }
  const meta = event.external_metadata;
  const htmlLink =
    meta?.htmlLink != null && meta.htmlLink.length > 0 ? meta.htmlLink : null;
  const calendarId =
    event.calendar_id != null && event.calendar_id.length > 0
      ? event.calendar_id
      : null;
  return {
    htmlLink,
    calendarId,
    status: resolveGoogleDisplayStatus(meta?.status),
    organizerEmail: resolveGoogleOrganizerEmail(meta?.organizer)
  };
};

const agendaFromCalendarEvent = ({
  event,
  dismissedIds,
  eventColors
}: {
  event: CalendarEvent;
  dismissedIds: Set<string>;
  eventColors: Record<string, string>;
}): DayPlanAgendaEvent => {
  const begins = event.event_begins;
  const ends = event.event_ends ?? null;
  return {
    externalId: event.external_id,
    title: event.summary,
    begins,
    ends,
    source: resolveDayPlanEventSource({
      externalId: event.external_id,
      platform: event.platform
    }),
    isDismissed: dismissedIds.has(event.external_id),
    colour: eventColors[event.external_id] ?? event.colour ?? null,
    durationSeconds: durationSecondsFromIsoRange(begins, ends),
    googleDetails: buildDayPlanAgendaGoogleDetails(event),
    seriesId: null
  };
};

const agendaFromEventTask = ({
  task,
  dismissedIds,
  eventColors
}: {
  task: ToDoTask;
  dismissedIds: Set<string>;
  eventColors: Record<string, string>;
}): DayPlanAgendaEvent | null => {
  const externalId = getDayPlanEventExternalId(task);
  if (externalId == null) {
    return null;
  }
  return {
    externalId,
    title: task.title,
    begins: null,
    ends: null,
    source: resolveDayPlanEventSource({ externalId }),
    isDismissed: dismissedIds.has(externalId),
    colour: eventColors[externalId] ?? null,
    durationSeconds: task.duration > 0 ? task.duration : 0,
    googleDetails: null,
    seriesId: null
  };
};

const overlayAgendaWithTask = ({
  existing,
  task,
  dismissedIds,
  eventColors
}: {
  existing: DayPlanAgendaEvent;
  task: ToDoTask;
  dismissedIds: Set<string>;
  eventColors: Record<string, string>;
}): DayPlanAgendaEvent => {
  const externalId = existing.externalId;
  const taskDuration = task.duration > 0 ? task.duration : 0;
  return {
    ...existing,
    title: existing.title.length > 0 ? existing.title : task.title,
    isDismissed: dismissedIds.has(externalId),
    colour: eventColors[externalId] ?? existing.colour,
    durationSeconds:
      existing.durationSeconds > 0 ? existing.durationSeconds : taskDuration
  };
};

const sortDayPlanAgendaEvents = (
  events: DayPlanAgendaEvent[]
): DayPlanAgendaEvent[] =>
  [...events].sort((left, right) => {
    if (left.begins == null && right.begins == null) {
      return 0;
    }
    if (left.begins == null) {
      return 1;
    }
    if (right.begins == null) {
      return -1;
    }
    return moment(left.begins).valueOf() - moment(right.begins).valueOf();
  });

const buildAgendaMapFromCalendar = ({
  calendarEvents,
  dismissedIds,
  eventColors
}: {
  calendarEvents: CalendarEvent[];
  dismissedIds: Set<string>;
  eventColors: Record<string, string>;
}): Map<string, DayPlanAgendaEvent> => {
  const byId = new Map<string, DayPlanAgendaEvent>();
  calendarEvents.forEach((event) => {
    byId.set(
      event.external_id,
      agendaFromCalendarEvent({ event, dismissedIds, eventColors })
    );
  });
  return byId;
};

const mergeEventTasksIntoAgendaMap = ({
  byId,
  tasks,
  dismissedIds,
  eventColors
}: {
  byId: Map<string, DayPlanAgendaEvent>;
  tasks: ToDoTask[];
  dismissedIds: Set<string>;
  eventColors: Record<string, string>;
}): void => {
  getDayPlanEventTasks(tasks).forEach((task) => {
    const externalId = getDayPlanEventExternalId(task);
    if (externalId == null) {
      return;
    }
    const existing = byId.get(externalId);
    if (existing != null) {
      byId.set(
        externalId,
        overlayAgendaWithTask({ existing, task, dismissedIds, eventColors })
      );
      return;
    }
    const fromTask = agendaFromEventTask({ task, dismissedIds, eventColors });
    if (fromTask != null) {
      byId.set(externalId, fromTask);
    }
  });
};

export const dayPlanOneOffEventsToCalendarEvents = (
  events: DayPlanOneOffEvent[]
): CalendarEvent[] =>
  events.map((event) => ({
    external_id: event.id,
    summary: event.title,
    event_begins: event.event_begins,
    event_ends: event.event_ends,
    platform: DayPlanEventSource.IMPORT
  }));

/**
 * Merge live calendar events, day-plan `event:` todos, and series occurrences.
 * Live / series times win; dismiss + colours come from day-plan; import-only rows stay.
 */
export const mergeDayPlanAgendaEvents = ({
  calendarEvents,
  tasks,
  dismissedEventIds = [],
  eventColors = {},
  eventSeriesOccurrences = [],
  dayPlanOneOffEvents = []
}: {
  calendarEvents: CalendarEvent[];
  tasks: ToDoTask[];
  dismissedEventIds?: string[];
  eventColors?: Record<string, string>;
  eventSeriesOccurrences?: EventSeriesOccurrence[];
  dayPlanOneOffEvents?: DayPlanOneOffEvent[];
}): DayPlanAgendaEvent[] => {
  const dismissedIds = new Set(dismissedEventIds);
  const byId = buildAgendaMapFromCalendar({
    calendarEvents: [
      ...calendarEvents,
      ...dayPlanOneOffEventsToCalendarEvents(dayPlanOneOffEvents)
    ],
    dismissedIds,
    eventColors
  });
  mergeEventTasksIntoAgendaMap({ byId, tasks, dismissedIds, eventColors });
  mergeSeriesOccurrencesIntoAgendaMap({
    byId,
    eventSeriesOccurrences,
    dismissedIds,
    eventColors
  });
  return sortDayPlanAgendaEvents([...byId.values()]);
};

const agendaFromSeriesOccurrence = ({
  occurrence,
  dismissedIds,
  eventColors
}: {
  occurrence: EventSeriesOccurrence;
  dismissedIds: Set<string>;
  eventColors: Record<string, string>;
}): DayPlanAgendaEvent => {
  const begins = occurrence.event_begins;
  const ends = occurrence.event_ends;
  return {
    externalId: occurrence.occurrence_id,
    title: occurrence.summary,
    begins,
    ends,
    source: DayPlanEventSource.SERIES,
    isDismissed: dismissedIds.has(occurrence.occurrence_id),
    colour: eventColors[occurrence.occurrence_id] ?? null,
    durationSeconds: durationSecondsFromIsoRange(begins, ends),
    googleDetails: null,
    seriesId: occurrence.series_id
  };
};

const mergeSeriesOccurrencesIntoAgendaMap = ({
  byId,
  eventSeriesOccurrences,
  dismissedIds,
  eventColors
}: {
  byId: Map<string, DayPlanAgendaEvent>;
  eventSeriesOccurrences: EventSeriesOccurrence[];
  dismissedIds: Set<string>;
  eventColors: Record<string, string>;
}): void => {
  eventSeriesOccurrences.forEach((occurrence) => {
    byId.set(
      occurrence.occurrence_id,
      agendaFromSeriesOccurrence({
        occurrence,
        dismissedIds,
        eventColors
      })
    );
  });
};

export const eventSeriesOccurrencesToCalendarEvents = (
  occurrences: EventSeriesOccurrence[]
): CalendarEvent[] =>
  occurrences.map((occurrence) => ({
    external_id: occurrence.occurrence_id,
    summary: occurrence.summary,
    event_begins: occurrence.event_begins,
    event_ends: occurrence.event_ends,
    platform: DayPlanEventSource.SERIES
  }));

export const partitionDayPlanAgendaEvents = (
  events: DayPlanAgendaEvent[]
): { active: DayPlanAgendaEvent[]; dismissed: DayPlanAgendaEvent[] } => {
  const active: DayPlanAgendaEvent[] = [];
  const dismissed: DayPlanAgendaEvent[] = [];
  events.forEach((event) => {
    if (event.isDismissed) {
      dismissed.push(event);
      return;
    }
    active.push(event);
  });
  return { active, dismissed };
};

export const isDayPlanDeepWorkTask = (task: ToDoTask): boolean =>
  parsePlannedBlockKey(task.planned_block_key)?.prefix ===
  PlannedBlockKeyPrefix.DEEP_WORK;

/**
 * Habits segment list: habit / Anytime only.
 * Timed deepwork lives on the timeline; meetings live under Events.
 */
export const getDayPlanHabitTasks = (tasks: ToDoTask[]): ToDoTask[] =>
  tasks.filter(
    (task) => !isDayPlanEventTask(task) && !isDayPlanDeepWorkTask(task)
  );

/**
 * Tasks panel empty-state copy. When the day's plan is entirely timeboxed into
 * Deep work, the panel is empty but the timeline is not — "Nothing planned"
 * would contradict it.
 */
export const getDayPlanEmptyStateCopyKeys = ({
  tasks,
  isPast
}: {
  tasks: ToDoTask[];
  isPast: boolean;
}): DayPlanEmptyStateCopyKeys => {
  const copyKeys = tasks.some(isDayPlanDeepWorkTask)
    ? DAY_PLAN_ALL_TIMEBOXED_COPY_KEYS
    : DAY_PLAN_EMPTY_PLANNED_COPY_KEYS;
  return isPast ? copyKeys.past : copyKeys.editable;
};

/**
 * Bucket planned tasks by project, preserving the incoming task order within
 * each bucket and ordering the buckets by first appearance. Tasks without a
 * project fall into a trailing bucket with no `project` (rendered as "Other tasks").
 */
export const groupDayPlanTasksByProject = ({
  tasks,
  projects
}: {
  tasks: ToDoTask[];
  projects: Project[];
}): DayPlanTaskGroup[] => {
  const projectNameById = new Map(
    projects.map((project) => [project.id, project.name])
  );
  const projectOrder: string[] = [];
  const tasksByProjectId = new Map<string, ToDoTask[]>();
  const tasksWithoutProject: ToDoTask[] = [];

  for (const task of tasks) {
    const projectId = task.project_id;
    if (projectId == null || projectId.length === 0) {
      tasksWithoutProject.push(task);
      continue;
    }
    if (!tasksByProjectId.has(projectId)) {
      tasksByProjectId.set(projectId, []);
      projectOrder.push(projectId);
    }
    tasksByProjectId.get(projectId)?.push(task);
  }

  const groups: DayPlanTaskGroup[] = projectOrder.map((projectId) => ({
    project: {
      id: projectId,
      name:
        projectNameById.get(projectId) ?? t('day_plan.tasks_unknown_project')
    },
    tasks: tasksByProjectId.get(projectId) ?? []
  }));

  if (hasAnyItems(tasksWithoutProject)) {
    groups.push({ tasks: tasksWithoutProject });
  }

  return groups;
};

export const getDayPlanTimelineTasks = (tasks: ToDoTask[]): ToDoTask[] =>
  tasks.filter(isDayPlanDeepWorkTask);

export const toTimeboxBlockKey = (epochMillis: number): string =>
  `${PlannedBlockKeyPrefix.DEEP_WORK}${BLOCK_KEY_SEPARATOR}${epochMillis}`;

export const toEventBlockKey = (externalId: string): string =>
  `${PlannedBlockKeyPrefix.EVENT}${BLOCK_KEY_SEPARATOR}${externalId}`;

export const isImportedDayPlanEventId = (externalId: string): boolean =>
  externalId.startsWith(
    `${DayPlanEventIdNamespace.IMPORT}${BLOCK_KEY_SEPARATOR}`
  );

export const toHabitBlockKey = (title: string): string | null => {
  const trimmed = title.trim();
  if (!trimmed.length) {
    return null;
  }
  return `${PlannedBlockKeyPrefix.HABIT}${BLOCK_KEY_SEPARATOR}${trimmed}`;
};

export const filterDismissedEvents = (
  events: CalendarEvent[],
  dismissedEventIds: string[]
): CalendarEvent[] => {
  const dismissed = new Set(dismissedEventIds);
  return events.filter((event) => !dismissed.has(event.external_id));
};

export const applyEventColours = (
  events: CalendarEvent[],
  colours: Record<string, string>
): CalendarEvent[] =>
  events.map((event) => {
    const colour = colours[event.external_id];
    return colour ? { ...event, colour } : event;
  });

export const getTaskStartMillis = (task: ToDoTask): number | null => {
  const parsed = parsePlannedBlockKey(task.planned_block_key);
  if (!parsed || parsed.prefix !== PlannedBlockKeyPrefix.DEEP_WORK) {
    return null;
  }
  const epoch = Number(parsed.value);
  return Number.isFinite(epoch) ? epoch : null;
};

const compareEntries = (a: TimelineEntry, b: TimelineEntry): number => {
  // Untimeboxed tasks (null start) sort after everything scheduled.
  if (a.startMillis === null && b.startMillis === null) {
    return 0;
  }
  if (a.startMillis === null) {
    return 1;
  }
  if (b.startMillis === null) {
    return -1;
  }
  return a.startMillis - b.startMillis;
};

interface AnchorInterval {
  start: number;
  end: number;
}

/** Anchored [start, end] intervals that deep-work gaps must not overlap. */
const collectAnchors = (
  events: CalendarEvent[],
  routines: RoutineBlock[]
): AnchorInterval[] => {
  const anchors: AnchorInterval[] = [];
  events.forEach((event) => {
    const start = moment(event.event_begins).valueOf();
    anchors.push({
      start,
      end: event.event_ends ? moment(event.event_ends).valueOf() : start
    });
  });
  routines.forEach((routine) =>
    anchors.push({ start: routine.startMillis, end: routine.endMillis })
  );
  return anchors.sort((a, b) => a.start - b.start);
};

const timeOnDayMillis = (day: string, time?: string): number | null => {
  if (!time) {
    return null;
  }
  const instant = moment(`${day} ${time}`, `${DATE_FORMAT_YMD} HH:mm`, true);
  return instant.isValid() ? instant.valueOf() : null;
};

/**
 * Free time between consecutive anchored items. Overlaps collapse via a running
 * `coveredUntil`; gaps below the minimum are skipped, and each block ends a short
 * buffer before the next item — matching the native app.
 */
const computeDeepWorkRangeList = (
  anchors: AnchorInterval[]
): DeepWorkRange[] => {
  if (anchors.length === 0) {
    return [];
  }
  const ranges: DeepWorkRange[] = [];
  let coveredUntil = anchors[0].end;
  for (let index = 1; index < anchors.length; index += 1) {
    const nextStart = anchors[index].start;
    const gapMinutes = (nextStart - coveredUntil) / MS_PER_MINUTE;
    if (gapMinutes >= DEEP_WORK_MIN_BLOCK_MINUTES) {
      ranges.push({
        startMillis: coveredUntil,
        endMillis: nextStart - DEEP_WORK_END_BUFFER_MINUTES * MS_PER_MINUTE
      });
    }
    coveredUntil = Math.max(coveredUntil, anchors[index].end);
  }
  return ranges;
};

const buildDeepWorkEntries = (
  ranges: DeepWorkRange[],
  tasks: ToDoTask[]
): { entries: TimelineEntry[]; nestedTaskIds: Set<string> } => {
  const nestedTaskIds = new Set<string>();
  const entries = ranges.map((range) => {
    const nestedTasks = collectTasksInDeepWorkRange(range, tasks);
    nestedTasks.forEach((task) => nestedTaskIds.add(task.id));
    return {
      kind: TimelineEntryKind.DEEP_WORK as const,
      startMillis: range.startMillis,
      endMillis: range.endMillis,
      tasks: nestedTasks
    };
  });
  return { entries, nestedTaskIds };
};

const collectTasksInDeepWorkRange = (
  range: DeepWorkRange,
  tasks: ToDoTask[]
): ToDoTask[] =>
  tasks
    .filter((task) => {
      const start = getTaskStartMillis(task);
      return (
        start != null && start >= range.startMillis && start < range.endMillis
      );
    })
    .sort(
      (a, b) => (getTaskStartMillis(a) ?? 0) - (getTaskStartMillis(b) ?? 0)
    );

/**
 * Deep-work free windows for a day (gaps between routines and calendar events).
 * Timeboxed `deepwork:` tasks are not anchors — they nest into these ranges.
 *
 * TODO(tomorrow): Call sites still pass events: [] — feed live calendarEvents
 * so ranges exclude meetings; UI should visualize available slots (DeepWorkPanel).
 */
export const getDeepWorkRanges = ({
  day,
  events,
  routines,
  dismissedEventIds
}: {
  day: string;
  events: CalendarEvent[];
  routines: RoutineBlock[];
  dismissedEventIds: string[];
}): DeepWorkRange[] => {
  const eventsForDay = events.filter(
    (event) => formatDateToYMD(event.event_begins) === day
  );
  const visibleEvents = filterDismissedEvents(eventsForDay, dismissedEventIds);
  return computeDeepWorkRangeList(collectAnchors(visibleEvents, routines));
};

/** Default `HH:mm` inside a deep-work gap (now if inside one today, else first/upcoming). */
export const getDefaultDeepWorkTimeOnly = ({
  ranges,
  nowMillis = Date.now(),
  preferNow = true
}: {
  ranges: DeepWorkRange[];
  nowMillis?: number;
  preferNow?: boolean;
}): string | null => {
  if (ranges.length === 0) {
    return null;
  }
  if (preferNow) {
    const preferred = preferNowDeepWorkTimeOnly(ranges, nowMillis);
    if (preferred != null) {
      return preferred;
    }
  }
  return formatMillisToTimeOnly(ranges[0].startMillis) ?? null;
};

const preferNowDeepWorkTimeOnly = (
  ranges: DeepWorkRange[],
  nowMillis: number
): string | null => {
  const containing = ranges.find(
    (range) => nowMillis >= range.startMillis && nowMillis < range.endMillis
  );
  if (containing != null) {
    return formatMillisToTimeOnly(nowMillis) ?? null;
  }
  const upcoming = ranges.find((range) => range.startMillis > nowMillis);
  if (upcoming != null) {
    return formatMillisToTimeOnly(upcoming.startMillis) ?? null;
  }
  return null;
};

export const isTimeInDeepWorkRanges = ({
  day,
  time,
  ranges
}: {
  day: string;
  time: string;
  ranges: DeepWorkRange[];
}): boolean => {
  if (ranges.length === 0 || time.length === 0) {
    return false;
  }
  const millis = timeOnDayMillis(day, time);
  if (millis == null) {
    return false;
  }
  return ranges.some(
    (range) => millis >= range.startMillis && millis < range.endMillis
  );
};

export const clampTimeToDeepWorkRanges = ({
  day,
  time,
  ranges
}: {
  day: string;
  time: string;
  ranges: DeepWorkRange[];
}): string | null => {
  if (ranges.length === 0) {
    return null;
  }
  const millis = timeOnDayMillis(day, time);
  if (millis == null) {
    return formatMillisToTimeOnly(ranges[0].startMillis) ?? null;
  }
  if (isTimeInDeepWorkRanges({ day, time, ranges })) {
    return formatMillisToTimeOnly(millis) ?? time;
  }
  return (
    formatMillisToTimeOnly(nearestRangeStartMillis(ranges, millis)) ?? null
  );
};

const nearestRangeStartMillis = (
  ranges: DeepWorkRange[],
  millis: number
): number => {
  let nearestStart = ranges[0].startMillis;
  let nearestDist = Math.abs(nearestStart - millis);
  for (let index = 1; index < ranges.length; index += 1) {
    const start = ranges[index].startMillis;
    const dist = Math.abs(start - millis);
    if (dist < nearestDist) {
      nearestStart = start;
      nearestDist = dist;
    }
  }
  return nearestStart;
};

/**
 * Merge events that overlap in time into clusters (interval sweep).
 * Non-overlapping singles stay as one-event clusters.
 */
export const clusterOverlappingCalendarEvents = (
  events: CalendarEvent[]
): { startMillis: number; endMillis: number; events: CalendarEvent[] }[] => {
  const sorted = [...events].sort(
    (left, right) =>
      moment(left.event_begins).valueOf() - moment(right.event_begins).valueOf()
  );
  const clusters: {
    startMillis: number;
    endMillis: number;
    events: CalendarEvent[];
  }[] = [];
  sorted.forEach((event) => {
    const startMillis = moment(event.event_begins).valueOf();
    const endMillis =
      event.event_ends != null && event.event_ends.length > 0
        ? moment(event.event_ends).valueOf()
        : startMillis;
    const last = clusters[clusters.length - 1];
    if (last != null && startMillis < last.endMillis) {
      last.events.push(event);
      last.endMillis = Math.max(last.endMillis, endMillis);
      return;
    }
    clusters.push({
      startMillis,
      endMillis,
      events: [event]
    });
  });
  return clusters.map(({ startMillis, endMillis, events: clustered }) => ({
    startMillis,
    endMillis,
    events: clustered
  }));
};

const toTimelineEventEntries = (events: CalendarEvent[]): TimelineEntry[] => {
  const clusters = clusterOverlappingCalendarEvents(events);
  const entries: TimelineEntry[] = [];
  clusters.forEach((cluster) => {
    if (cluster.events.length === 1) {
      entries.push({
        kind: TimelineEntryKind.EVENT,
        startMillis: cluster.startMillis,
        event: cluster.events[0]
      });
      return;
    }
    entries.push({
      kind: TimelineEntryKind.EVENT_CLUSTER,
      startMillis: cluster.startMillis,
      endMillis: cluster.endMillis,
      events: cluster.events
    });
  });
  return entries;
};

/**
 * Merge routines, planned todos, and live calendar events into one time-ordered
 * timeline. Deep-work gaps are free windows between routines/events; `deepwork:`
 * tasks whose start falls in a gap nest inside that block (orphans stay flat).
 * Events from other days and dismissed meetings are dropped; the rest are
 * colour-tinted. Overlapping events become one expandable cluster.
 */
export const buildTimeline = ({
  tasks,
  events,
  routines = [],
  dismissedEventIds,
  eventColors,
  selectedDay
}: {
  tasks: ToDoTask[];
  events: CalendarEvent[];
  routines?: RoutineBlock[];
  dismissedEventIds: string[];
  eventColors: Record<string, string>;
  selectedDay: string;
}): TimelineEntry[] => {
  const eventsForDay = events.filter(
    (event) => formatDateToYMD(event.event_begins) === selectedDay
  );

  const visibleEvents = applyEventColours(
    filterDismissedEvents(eventsForDay, dismissedEventIds),
    eventColors
  );

  const eventEntries = toTimelineEventEntries(visibleEvents);

  const routineEntries: TimelineEntry[] = routines.map((routine) => ({
    kind: TimelineEntryKind.ROUTINE,
    startMillis: routine.startMillis,
    routine
  }));

  const deepWorkRanges = computeDeepWorkRangeList(
    collectAnchors(visibleEvents, routines)
  );
  const { entries: deepWorkEntries, nestedTaskIds } = buildDeepWorkEntries(
    deepWorkRanges,
    tasks
  );

  const taskEntries: TimelineEntry[] = tasks
    .filter((task) => !nestedTaskIds.has(task.id))
    .map((task) => ({
      kind: TimelineEntryKind.TASK,
      startMillis: getTaskStartMillis(task),
      task
    }));

  return [
    ...eventEntries,
    ...taskEntries,
    ...routineEntries,
    ...deepWorkEntries
  ].sort(compareEntries);
};

export const sortByPlannedOrder = (tasks: ToDoTask[]): ToDoTask[] =>
  [...tasks].sort(
    (a, b) =>
      (a.planned_order ?? Number.MAX_SAFE_INTEGER) -
      (b.planned_order ?? Number.MAX_SAFE_INTEGER)
  );

/**
 * Tasks segment order: timed (`deepwork:`) earliest→latest, then Anytime /
 * habit by `planned_order`.
 */
export const sortPlannedWorkTasks = (tasks: ToDoTask[]): ToDoTask[] =>
  [...tasks].sort((a, b) => {
    const aStart = getTaskStartMillis(a);
    const bStart = getTaskStartMillis(b);
    if (aStart != null && bStart != null) {
      return aStart - bStart;
    }
    if (aStart != null) {
      return -1;
    }
    if (bStart != null) {
      return 1;
    }
    return (
      (a.planned_order ?? Number.MAX_SAFE_INTEGER) -
      (b.planned_order ?? Number.MAX_SAFE_INTEGER)
    );
  });

export const canReorderPlannedTask = (task: ToDoTask): boolean =>
  getTaskStartMillis(task) == null;

/**
 * Next habit/anytime neighbour in `direction` within an ordered work-task list.
 * Skips deepwork rows so reorder never swaps with a timeboxed task.
 */
export const findReorderablePlannedNeighbour = ({
  tasks,
  index,
  direction
}: {
  tasks: ToDoTask[];
  index: number;
  direction: DayNavDirection;
}): { neighbour: ToDoTask; neighbourIndex: number } | null => {
  const delta = getDayNavDelta(direction);
  if (delta == null || index < 0 || index >= tasks.length) {
    return null;
  }
  let neighbourIndex = index + delta;
  while (neighbourIndex >= 0 && neighbourIndex < tasks.length) {
    const neighbour = tasks[neighbourIndex];
    if (canReorderPlannedTask(neighbour)) {
      return { neighbour, neighbourIndex };
    }
    neighbourIndex += delta;
  }
  return null;
};

/**
 * Build the two `planTask` patches that swap adjacent planned-order indexes.
 * Caller dispatches; this stays free of Redux.
 */
export const buildPlannedOrderSwap = ({
  task,
  neighbour,
  index,
  swapIndex
}: {
  task: ToDoTask;
  neighbour: ToDoTask;
  index: number;
  swapIndex: number;
}): { first: PlanTaskArgs; second: PlanTaskArgs } => ({
  first: {
    task,
    plannedOrder: neighbour.planned_order ?? swapIndex
  },
  second: {
    task: neighbour,
    plannedOrder: task.planned_order ?? index
  }
});

const pushTopMetaPart = (
  parts: DayPlanTaskMetaPart[],
  task: ToDoTask
): void => {
  if (task.top_score == null || task.top_score <= 0) {
    return;
  }
  parts.push({
    kind: DayPlanTaskMetaKind.TOP,
    text: t('to_do_procrastinate.top_score', {
      score: roundToDecimals(task.top_score).toString()
    })
  });
};

const pushDatedMetaPart = ({
  parts,
  kind,
  dateValue,
  labelKey
}: {
  parts: DayPlanTaskMetaPart[];
  kind: DayPlanTaskMetaKind;
  dateValue?: string | null;
  labelKey: string;
}): void => {
  const label = formatDueDate(dateValue);
  if (label.length === 0) {
    return;
  }
  parts.push({ kind, text: t(labelKey, { date: label }) });
};

export const getDayPlanTaskMetaParts = (
  task: ToDoTask
): DayPlanTaskMetaPart[] => {
  const parts: DayPlanTaskMetaPart[] = [];
  pushTopMetaPart(parts, task);
  pushDatedMetaPart({
    parts,
    kind: DayPlanTaskMetaKind.DUE,
    dateValue: task.due_date,
    labelKey: 'to_do_procrastinate.due'
  });
  pushDatedMetaPart({
    parts,
    kind: DayPlanTaskMetaKind.CREATED,
    dateValue: task.created_at,
    labelKey: 'to_do_procrastinate.created'
  });

  return parts;
};

/* ── File import helpers live in day-plan-file-import.util.ts ── */

export const collectCalendarAccounts = (
  calendars: Calendars[] | undefined
): string[] =>
  (calendars ?? [])
    .filter((row) => isCalendarAccountFetchable(row))
    .map((row) => row.account);

export const isCalendarAccountFetchable = (row: Calendars): boolean => {
  if (row.expired || row.requires_reauth === true) {
    return false;
  }
  if (row.account.length === 0) {
    return false;
  }
  return (row.items ?? []).some((item) => item.is_Selected);
};

/**
 * Provider calendar ids for selected calendars on healthy accounts.
 * Uses `calendar_id` only — DB row `id` does not match event.calendar_id.
 */
export const collectSelectedCalendarIds = (calendars?: Calendars[]): string[] =>
  (calendars ?? [])
    .filter((row) => !row.expired && row.requires_reauth !== true)
    .flatMap((row) => row.items ?? [])
    .filter((item) => item.is_Selected && item.calendar_id?.length > 0)
    .map((item) => item.calendar_id);

export const buildCalendarEventsParams = (
  args: BuildCalendarEventsParamsArgs
): Record<string, string> => {
  const params: Record<string, string> = {};
  if ('days' in args && args.days != null) {
    params[CALENDAR_EVENTS_QUERY_PARAM.DAYS] = String(args.days);
  } else if ('day' in args && args.day != null) {
    params[CALENDAR_EVENTS_QUERY_PARAM.DAY] = args.day;
  }
  if (args.account != null && args.account.length > 0) {
    params[CALENDAR_EVENTS_QUERY_PARAM.ACCOUNT] = args.account;
  }
  return params;
};

/**
 * Keep events from selected calendars only.
 * Empty selected list → no events (BE always returns provider calendar_id).
 */
export const filterEventsBySelectedCalendarIds = (
  events: CalendarEvent[],
  selectedCalendarIds: string[]
): CalendarEvent[] => {
  if (selectedCalendarIds.length === 0) {
    return [];
  }
  const selected = new Set(selectedCalendarIds);
  return events.filter(
    (event) =>
      event.calendar_id != null &&
      event.calendar_id.length > 0 &&
      selected.has(event.calendar_id)
  );
};

export const platformNeedsCalendarReconnect = (
  calendars: Calendars[] | undefined
): boolean =>
  (calendars ?? []).some((row) => row.requires_reauth === true || row.expired);

export const buildAccountsByPlatform = ({
  isGoogleConnected,
  isMicrosoftCalendarConnected,
  googleCalendars,
  microsoftCalendars
}: {
  isGoogleConnected: boolean;
  isMicrosoftCalendarConnected: boolean;
  googleCalendars: Calendars[] | undefined;
  microsoftCalendars: Calendars[] | undefined;
}): Record<string, string[]> => {
  const accountsByPlatform: Record<string, string[]> = {};
  if (isGoogleConnected) {
    accountsByPlatform[INTEGRATION_PLATFORM.GOOGLE] =
      collectCalendarAccounts(googleCalendars);
  }
  if (isMicrosoftCalendarConnected) {
    accountsByPlatform[INTEGRATION_PLATFORM.MICROSOFT] =
      collectCalendarAccounts(microsoftCalendars);
  }
  return accountsByPlatform;
};

export const collectSelectedCalendarIdsByConnection = ({
  isGoogleConnected,
  isMicrosoftCalendarConnected,
  googleCalendars,
  microsoftCalendars
}: {
  isGoogleConnected: boolean;
  isMicrosoftCalendarConnected: boolean;
  googleCalendars: Calendars[] | undefined;
  microsoftCalendars: Calendars[] | undefined;
}): string[] => {
  const ids: string[] = [];
  if (isGoogleConnected) {
    ids.push(...collectSelectedCalendarIds(googleCalendars));
  }
  if (isMicrosoftCalendarConnected) {
    ids.push(...collectSelectedCalendarIds(microsoftCalendars));
  }
  return ids;
};

export const hasCalendarAccounts = (
  accountsByPlatform: Record<string, string[]>
): boolean => {
  return Object.values(accountsByPlatform).some(
    (accounts) => accounts.length > 0
  );
};

export const timeboxKeyForDay = (day: string, time: string): string | null => {
  if (!time) {
    return null;
  }
  const instant = moment(`${day} ${time}`, `${DATE_FORMAT_YMD} HH:mm`, true);
  return instant.isValid() ? toTimeboxBlockKey(instant.valueOf()) : null;
};

export const formatClockLabel = (millis: number): string =>
  moment(millis).format('hh:mm a');

export const formatTimeRange = (
  startMillis: number,
  endMillis: number
): string =>
  `${formatClockLabel(startMillis)} – ${formatClockLabel(endMillis)}`;

export const formatEventTime = (isoInstant: string): string => {
  const parsed = moment(isoInstant);
  return parsed.isValid() ? formatClockLabel(parsed.valueOf()) : '';
};

export const formatEventTimeRange = (
  eventBegins: string,
  eventEnds?: string | null
): string => {
  const startLabel = formatEventTime(eventBegins);
  if (eventEnds == null || eventEnds.length === 0) {
    return startLabel;
  }
  const endLabel = formatEventTime(eventEnds);
  return endLabel.length > 0 ? `${startLabel} – ${endLabel}` : startLabel;
};

export const toHexColorWithAlpha = (
  color: string,
  alphaHex: string = EVENT_COLOR_HEX_ALPHA.BACKGROUND_TINT
): string => `${color}${alphaHex}`;

/**
 * Blend an event accent toward light/dark ink so links stay readable on tinted rows.
 * Prefer browsers that support `color-mix`; callers should keep a CSS class fallback.
 */
export const blendEventAccentLinkColor = (
  eventColor: string,
  isDark: boolean
): string => {
  const contrast = isDark
    ? EVENT_ACCENT_LINK_COLOR_MIX.DARK_CONTRAST
    : EVENT_ACCENT_LINK_COLOR_MIX.LIGHT_CONTRAST;
  return `color-mix(in srgb, ${eventColor} ${EVENT_ACCENT_LINK_COLOR_MIX.EVENT_PERCENT}%, ${contrast} ${EVENT_ACCENT_LINK_COLOR_MIX.CONTRAST_PERCENT}%)`;
};

const weekdayTokenForDay = (day: string): DaysOfWeek =>
  moment(day, DATE_FORMAT_YMD)
    .locale('en')
    .format(SHORT_DAY_NAME)
    .toUpperCase() as DaysOfWeek;

const activitiesScheduledOn = (
  activities: ActivityType[],
  weekday: DaysOfWeek
): ActivityType[] =>
  activities.filter((activity) =>
    normalizeDaysOfWeekValue(activity.days_of_week).includes(weekday)
  );

/**
 * Lay activities end-to-end from a start instant, each consuming its
 * `duration_seconds`. Returns the timed rows and the instant the block ends.
 */
const layoutActivities = (
  activities: ActivityType[],
  startMillis: number
): { activities: RoutineActivity[]; endMillis: number } => {
  let cursor = startMillis;
  const rows = activities.map((activity) => {
    const start = cursor;
    cursor += (activity.duration_seconds ?? 0) * MS_PER_SECOND;
    return {
      id: activity.id,
      emoji: activity.habit_icon ?? '',
      label: activity.name,
      startMillis: start,
      endMillis: cursor
    };
  });
  return { activities: rows, endMillis: cursor };
};

const buildRoutineBlock = ({
  id,
  title,
  emoji,
  startMillis,
  activities,
  sequenceId
}: {
  id: string;
  title: string;
  emoji: string;
  startMillis: number | null;
  activities: ActivityType[];
  sequenceId?: string;
}): RoutineBlock | null => {
  if (startMillis === null || activities.length === 0) {
    return null;
  }
  const { activities: rows, endMillis } = layoutActivities(
    activities,
    startMillis
  );
  return {
    id,
    title,
    emoji,
    startMillis,
    endMillis,
    activities: rows,
    ...(sequenceId != null ? { sequenceId } : {})
  };
};

/** A custom routine appears only when scheduled (`ON_SCHEDULE`) to this weekday. */
const buildCustomRoutineBlock = (
  routine: CustomRoutineType,
  day: string,
  weekday: DaysOfWeek
): RoutineBlock | null => {
  if (routine.trigger !== CUSTOM_ROUTINE_TRIGGER.ON_SCHEDULE) {
    return null;
  }
  if (!normalizeDaysOfWeekValue(routine.days_of_week).includes(weekday)) {
    return null;
  }
  return buildRoutineBlock({
    id: `${CUSTOM_ROUTINE_BLOCK_ID_PREFIX}${routine.id}`,
    title: routine.name,
    emoji: CUSTOM_ROUTINE_EMOJI,
    startMillis: timeOnDayMillis(day, routine.start_time),
    activities: activitiesScheduledOn(routine.standalone_activities, weekday),
    sequenceId: routine.activity_sequence_id
  });
};

/**
 * Build the morning, evening, and scheduled-custom routine blocks that fall on a
 * given day, mapped to timeline `RoutineBlock`s. Morning is anchored at
 * `startup_time`, evening at `shutdown_time`; each activity is laid end-to-end by
 * its `duration_seconds`. Activities (and custom routines) not scheduled for the
 * day's weekday are dropped, and a block with no activities that day is omitted.
 */
export const buildRoutineBlocksForDay = ({
  settings,
  day,
  morningTitle,
  eveningTitle
}: {
  settings: SettingsType;
  day: string;
  morningTitle: string;
  eveningTitle: string;
}): RoutineBlock[] => {
  const weekday = weekdayTokenForDay(day);
  const blocks: (RoutineBlock | null)[] = [
    buildRoutineBlock({
      id: MORNING_ROUTINE_BLOCK_ID,
      title: morningTitle,
      emoji: MORNING_ROUTINE_EMOJI,
      startMillis: timeOnDayMillis(day, settings.startup_time),
      activities: activitiesScheduledOn(
        settings.morning_activities ?? [],
        weekday
      )
    }),
    buildRoutineBlock({
      id: EVENING_ROUTINE_BLOCK_ID,
      title: eveningTitle,
      emoji: EVENING_ROUTINE_EMOJI,
      startMillis: timeOnDayMillis(day, settings.shutdown_time),
      activities: activitiesScheduledOn(
        settings.evening_activities ?? [],
        weekday
      )
    }),
    ...(settings.custom_routines ?? []).map((routine) =>
      buildCustomRoutineBlock(routine, day, weekday)
    )
  ];
  return blocks.filter((block): block is RoutineBlock => block !== null);
};

export const entryKey = (entry: TimelineEntry): string => {
  switch (entry.kind) {
    case TimelineEntryKind.EVENT:
      return `event-${entry.event.external_id}`;
    case TimelineEntryKind.EVENT_CLUSTER:
      return `event-cluster-${entry.startMillis}-${entry.events
        .map((event) => event.external_id)
        .join('-')}`;
    case TimelineEntryKind.TASK:
      return `task-${entry.task.id}`;
    case TimelineEntryKind.ROUTINE:
      return `routine-${entry.routine.id}`;
    default:
      return `deep-work-${entry.startMillis}`;
  }
};

const hashString = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
};

export const getTimelineTaskColor = (task: {
  id: string;
  planned_block_key?: string | null;
}): string => {
  if (getTaskStartMillis(task as ToDoTask) == null) {
    const paletteLength = UNTIMED_TASK_COLOUR_PALETTE.length;
    const index = hashString(task.id) % paletteLength;
    return UNTIMED_TASK_COLOUR_PALETTE[index];
  }
  return COLOR.SEMANTIC.SUCCESS;
};

export const isGoogleCalendarPlatform = (platform: string): boolean =>
  platform.toLowerCase().includes(INTEGRATION_PLATFORM.GOOGLE.toLowerCase());

export const isMicrosoftCalendarPlatform = (platform: string): boolean =>
  platform.toLowerCase().includes(INTEGRATION_PLATFORM.MICROSOFT.toLowerCase());

/* ── Schedule drawer helpers (start) ── */

export const SCHEDULE_BLOCK_KIND_ORDER = [
  PlannedBlockKeyPrefix.DEEP_WORK,
  PlannedBlockKeyPrefix.HABIT
] as const;

export type ScheduleDrawerBlockKind =
  (typeof SCHEDULE_BLOCK_KIND_ORDER)[number];

/** Map unsupported kinds (e.g. Meeting) to a drawer segment. */
export const resolveScheduleDrawerBlockKind = (
  blockKind: PlannedBlockKeyPrefix
): ScheduleDrawerBlockKind =>
  SCHEDULE_BLOCK_KIND_ORDER.includes(blockKind as ScheduleDrawerBlockKind)
    ? (blockKind as ScheduleDrawerBlockKind)
    : PlannedBlockKeyPrefix.DEEP_WORK;

export const getScheduleBlockKindTabIndex = (
  blockKind: PlannedBlockKeyPrefix
): number => {
  const index = SCHEDULE_BLOCK_KIND_ORDER.indexOf(
    resolveScheduleDrawerBlockKind(blockKind)
  );
  return index >= 0 ? index : 0;
};

export const getScheduleBlockKindByTabIndex = (
  index: number
): PlannedBlockKeyPrefix =>
  SCHEDULE_BLOCK_KIND_ORDER[index] ?? PlannedBlockKeyPrefix.DEEP_WORK;

export const resolveScheduleInitialTime = ({
  task,
  ranges,
  selectedDay
}: {
  task: ToDoTask;
  ranges: DeepWorkRange[];
  selectedDay: string;
}): string | undefined => {
  const existing = formatMillisToTimeOnly(getTaskStartMillis(task));
  if (existing != null) {
    return (
      clampTimeToDeepWorkRanges({
        day: selectedDay,
        time: existing,
        ranges
      }) ?? existing
    );
  }
  return (
    getDefaultDeepWorkTimeOnly({
      ranges,
      preferNow: isDayToday(selectedDay)
    }) ?? undefined
  );
};

export const resolveSchedulePlanBlockKey = ({
  blockKind,
  taskTitle,
  selectedDay,
  selectedTime
}: {
  blockKind: PlannedBlockKeyPrefix;
  taskTitle: string;
  selectedDay: string;
  selectedTime: string;
}): string | null => {
  if (blockKind === PlannedBlockKeyPrefix.HABIT) {
    return toHabitBlockKey(taskTitle);
  }
  if (selectedTime.length === 0) {
    return null;
  }
  return timeboxKeyForDay(selectedDay, selectedTime);
};

export const buildScheduleAddPlanArgs = ({
  task,
  selectedDay,
  plannedTasksCount,
  selectedTime,
  blockKind
}: {
  task: ToDoTask;
  selectedDay: string;
  plannedTasksCount: number;
  selectedTime: string;
  blockKind: PlannedBlockKeyPrefix;
}): PlanTaskArgs | null => {
  const plannedBlockKey = resolveSchedulePlanBlockKey({
    blockKind,
    taskTitle: task.title,
    selectedDay,
    selectedTime
  });
  if (plannedBlockKey == null) {
    return null;
  }
  return {
    task,
    plannedDate: selectedDay,
    plannedOrder: plannedTasksCount,
    plannedBlockKey
  };
};

export const buildScheduleEditPlanArgs = ({
  task,
  selectedDay,
  selectedTime,
  blockKind
}: {
  task: ToDoTask;
  selectedDay: string;
  selectedTime: string;
  blockKind: PlannedBlockKeyPrefix;
}): PlanTaskArgs | null => {
  const plannedBlockKey = resolveSchedulePlanBlockKey({
    blockKind,
    taskTitle: task.title,
    selectedDay,
    selectedTime
  });
  if (plannedBlockKey == null) {
    return null;
  }
  return { task, plannedBlockKey };
};

/* ── Schedule drawer helpers (end) ── */

export {
  buildCreateDayPlanEventItems,
  createInitialImportOptions,
  createInitialFileImportUi,
  getDefaultFileImportOpenDate,
  getDefaultRecurringEventsEndOn,
  getExtractStatusPatch,
  getFileImportReviewSummary,
  getUploadErrorKey,
  groupFileImportEventsByDate,
  hasValidFileImportTimeRange,
  isDayPlanImportImageMime,
  isLowConfidence,
  normalizeFileImportEventDate,
  normalizeFileImportEvents,
  normalizeFileImportEventSeries,
  normalizeFileImportUnscheduledEntries,
  resolveUnscheduledPromotion,
  toImportedEventBlockKey,
  replacePreviewObjectUrl
} from 'utils/day-plan/day-plan-file-import.util';

export {
  buildCreateEventSeriesItems,
  buildEventSeriesRRULE,
  countOccurrences,
  formatEventSeriesRecurrenceLabel,
  formatEventSeriesScheduleLabel,
  formatEventSeriesStartDateLabel,
  formatEventSeriesTimeRangeLabel,
  parseEventSeriesRRULE,
  weekdayToByDay
} from 'utils/day-plan/event-series-rrule.util';
